import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { NarrationEditor } from "./NarrationEditor";
import { VoicePicker } from "./VoicePicker";
import { CaseFileEditor } from "./CaseFileEditor";
import { ChallengeSettings } from "./ChallengeSettings";

export const MessageEditor = ({ onSaved }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings", { ...form, target_count: Number(form.target_count), idle_minutes: Number(form.idle_minutes) });
      toast.success("Experiment text saved");
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err, "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  if (!form) return null;
  return (
    <form data-testid="settings-form" onSubmit={save} className="grid gap-6">
      <section className="grid gap-5 border border-slate-800 bg-slate-950/70 p-6">
        <p className="lab-label !text-cyan-300">Kiosk</p>
        <div className="grid gap-5 sm:grid-cols-[1fr_160px_160px]">
          <div>
            <label className="lab-label">Experiment title (kiosk heading)</label>
            <input data-testid="settings-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="lab-input mt-2 w-full" />
          </div>
          <div>
            <label className="lab-label">Target vocabulary</label>
            <input data-testid="settings-target-input" type="number" min="1" max="500" required value={form.target_count} onChange={(e) => setForm({ ...form, target_count: e.target.value })} className="lab-input mt-2 w-full font-mono" />
          </div>
          <div>
            <label className="lab-label">Idle reset (minutes)</label>
            <input data-testid="settings-idle-input" type="number" min="1" max="60" required value={form.idle_minutes} onChange={(e) => setForm({ ...form, idle_minutes: e.target.value })} className="lab-input mt-2 w-full font-mono" />
          </div>
        </div>
        <div>
          <label className="lab-label">The hive's final message (final screen) - use {"{subject}"}, {"{nickname}"}, {"{father}"}</label>
          <textarea data-testid="settings-final-message-input" required rows={6} value={form.final_message} onChange={(e) => setForm({ ...form, final_message: e.target.value })} className="lab-input mt-2 w-full font-mono" />
        </div>
      </section>
      <ChallengeSettings value={form.challenges} onChange={(challenges) => setForm({ ...form, challenges })} />
      <section className="grid gap-5 border border-amber-400/30 bg-slate-950/70 p-6">
        <div>
          <p className="scientist-label">Case file // the missing researcher</p>
          <p className="mt-1 text-xs text-slate-500">Every memory the AI writes stays consistent with these facts. Change them, save, then "Rewrite all memories" on the Objects tab if you want existing memories to follow.</p>
        </div>
        <CaseFileEditor caseFile={form.case_file} onChange={(case_file) => setForm({ ...form, case_file })} />
        <div>
          <p className="lab-label">Monroe's voice (the memories)</p>
          <div className="mt-2"><VoicePicker value={form.monroe_voice_id} onChange={(monroe_voice_id) => setForm({ ...form, monroe_voice_id })} style="memory" testId="monroe-voice" /></div>
        </div>
      </section>
      <section className="grid gap-5 border border-amber-400/30 bg-slate-950/70 p-6">
        <div>
          <p className="lab-label !text-amber-300">The Scientist // memory specialist narration</p>
          <p className="mt-1 text-xs text-slate-500">Spoken aloud on the kiosk with an ElevenLabs voice. Pick the voice, then edit what the Scientist says at each point.</p>
        </div>
        <VoicePicker value={form.narrator_voice_id} onChange={(narrator_voice_id) => setForm({ ...form, narrator_voice_id })} />
        <NarrationEditor narration={form.narration} onChange={(narration) => setForm({ ...form, narration })} />
      </section>
      <div className="flex justify-end">
        <button data-testid="settings-save-button" disabled={busy} className="lab-btn-primary">{busy ? "Saving..." : "Save text & voice"}</button>
      </div>
    </form>
  );
};
